import { useState, useEffect, useRef } from 'react';
import { Volume2, VolumeX, Play, Pause, Square, Trash2, MapPin, Clock, Plus, Minus } from 'lucide-react';
import { toast } from 'sonner';
import TopNav from '@/components/TopNav';

// Vintage Microphone SVG Component inspired by the AEA ribbon mic
const VintageMicIcon = ({ className = "w-16 h-16", recording = false }) => (
  <svg viewBox="0 0 100 140" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Mic mesh grille */}
    <rect x="20" y="5" width="60" height="70" rx="8" fill={recording ? "#EF4444" : "#9CA3AF"} stroke="#71717A" strokeWidth="2"/>
    <pattern id="meshPattern" patternUnits="userSpaceOnUse" width="6" height="6">
      <circle cx="3" cy="3" r="2" fill={recording ? "#DC2626" : "#6B7280"}/>
    </pattern>
    <rect x="24" y="9" width="52" height="62" rx="4" fill="url(#meshPattern)"/>
    
    {/* Center band with logo area */}
    <rect x="20" y="75" width="60" height="20" fill="#52525B" stroke="#71717A" strokeWidth="2"/>
    <ellipse cx="50" cy="85" rx="12" ry="6" fill={recording ? "#EF4444" : "#DC2626"} opacity="0.9"/>
    
    {/* Gold side knobs */}
    <circle cx="15" cy="45" r="5" fill="#D97706"/>
    <circle cx="85" cy="45" r="5" fill="#D97706"/>
    
    {/* Mount bracket */}
    <path d="M35 95 L35 105 Q50 115 65 105 L65 95" stroke="#A1A1AA" strokeWidth="3" fill="none"/>
    
    {/* Stand connector */}
    <rect x="45" y="105" width="10" height="15" fill="#71717A" rx="2"/>
    <circle cx="50" cy="125" r="8" fill="#52525B" stroke="#71717A" strokeWidth="2"/>
    
    {/* Gold ring */}
    <circle cx="50" cy="125" r="5" fill="#D97706"/>
    
    {/* Cable */}
    <path d="M30 90 Q15 100 20 120" stroke="#1F2937" strokeWidth="3" fill="none"/>
  </svg>
);

export default function Coxswain() {
  // Hardware Detection State
  const [hardwareDetected, setHardwareDetected] = useState(false);
  const [demoMode, setDemoMode] = useState(false);
  const [audioOutputs, setAudioOutputs] = useState([]);
  const [checkingHardware, setCheckingHardware] = useState(true);
  
  // Cox Mode State
  const [coxModeEnabled, setCoxModeEnabled] = useState(false);
  
  // Volume Control State
  const [volume, setVolume] = useState(80); // 0-100
  const [isMuted, setIsMuted] = useState(false);
  const [previousVolume, setPreviousVolume] = useState(80);
  
  // Recording State
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordings, setRecordings] = useState([]);
  const [playingId, setPlayingId] = useState(null);
  
  // GPS State
  const [gpsEnabled, setGpsEnabled] = useState(false);
  const [currentPosition, setCurrentPosition] = useState(null);
  const [recordingPath, setRecordingPath] = useState([]);
  
  // Refs
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingIntervalRef = useRef(null);
  const audioPlayerRef = useRef(null);
  const gpsWatchIdRef = useRef(null);
  
  // Check if features are available (hardware detected OR demo mode)
  const featuresAvailable = hardwareDetected || demoMode;
  
  // Check for audio hardware on mount
  useEffect(() => {
    const checkHardware = async () => {
      setCheckingHardware(true);
      try {
        // Check if mediaDevices API is available
        if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
          console.warn('MediaDevices API not available');
          setHardwareDetected(false);
          setCheckingHardware(false);
          return;
        }
        
        // Check for audio output devices (speakers, amplifiers)
        const devices = await navigator.mediaDevices.enumerateDevices();
        const outputs = devices.filter(d => d.kind === 'audiooutput');
        const inputs = devices.filter(d => d.kind === 'audioinput');
        
        setAudioOutputs(outputs);
        
        // Log available devices for debugging
        console.log('Audio inputs:', inputs.length, 'Audio outputs:', outputs.length);
        
        // Consider hardware detected if there's more than just the default output
        // or if there's any output device at all
        const hasExternalAudio = outputs.length > 1 || 
          outputs.some(d => d.label && !d.label.toLowerCase().includes('default'));
        
        // Check if there's at least one microphone
        const hasMicrophone = inputs.length > 0;
        
        // Set hardware detected based on microphone availability
        // (we need at least a microphone for recording)
        setHardwareDetected(hasMicrophone || hasExternalAudio);
        
      } catch (error) {
        console.error('Hardware detection error:', error);
        setHardwareDetected(false);
      }
      setCheckingHardware(false);
    };
    
    checkHardware();
    
    // Listen for device changes
    if (navigator.mediaDevices) {
      navigator.mediaDevices.addEventListener('devicechange', checkHardware);
      return () => navigator.mediaDevices.removeEventListener('devicechange', checkHardware);
    }
  }, []);
  
  // Load settings on mount
  useEffect(() => {
    const savedCoxMode = localStorage.getItem('cox_mode_enabled');
    if (savedCoxMode === 'true') {
      setCoxModeEnabled(true);
    }
    
    const savedDemoMode = localStorage.getItem('cox_demo_mode');
    if (savedDemoMode === 'true') {
      setDemoMode(true);
    }
    
    // Load saved recordings from localStorage
    const savedRecordings = localStorage.getItem('cox_recordings');
    if (savedRecordings) {
      try {
        setRecordings(JSON.parse(savedRecordings));
      } catch (e) {
        console.error('Failed to load recordings:', e);
      }
    }
  }, []);
  
  // Toggle Demo Mode
  const toggleDemoMode = () => {
    const newValue = !demoMode;
    setDemoMode(newValue);
    localStorage.setItem('cox_demo_mode', newValue.toString());
    toast.success(newValue ? 'Demo Mode enabled - features unlocked' : 'Demo Mode disabled');
  };
  
  // Save Cox mode setting
  const toggleCoxMode = () => {
    if (!featuresAvailable) {
      toast.error('Hardware not detected. Enable Demo Mode to use Cox features.');
      return;
    }
    const newValue = !coxModeEnabled;
    setCoxModeEnabled(newValue);
    localStorage.setItem('cox_mode_enabled', newValue.toString());
    toast.success(newValue ? 'Cox Mode enabled' : 'Cox Mode disabled');
  };
  
  // Volume Control
  const adjustVolume = (direction) => {
    if (!featuresAvailable) return;
    
    const step = 10; // 10% steps
    let newVolume;
    
    if (direction === 'up') {
      newVolume = Math.min(100, volume + step);
    } else {
      newVolume = Math.max(0, volume - step);
    }
    
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
    
    // Update audio player if present
    if (audioPlayerRef.current) {
      audioPlayerRef.current.volume = newVolume / 100;
      audioPlayerRef.current.muted = newVolume === 0;
    }
    
    toast.info(`Volume: ${newVolume}%`);
  };
  
  // Mute/Unmute Toggle
  const toggleMute = () => {
    if (!featuresAvailable) return;
    
    if (isMuted) {
      // Unmute - restore previous volume
      const restoredVolume = previousVolume > 0 ? previousVolume : 50;
      setVolume(restoredVolume);
      setIsMuted(false);
      
      if (audioPlayerRef.current) {
        audioPlayerRef.current.volume = restoredVolume / 100;
        audioPlayerRef.current.muted = false;
      }
      
      toast.info(`Volume: ${restoredVolume}%`);
    } else {
      // Mute - save current volume and set to 0
      setPreviousVolume(volume);
      setVolume(0);
      setIsMuted(true);
      
      if (audioPlayerRef.current) {
        audioPlayerRef.current.volume = 0;
        audioPlayerRef.current.muted = true;
      }
      
      toast.info('Muted');
    }
  };
  
  // Start GPS tracking
  const startGPSTracking = () => {
    if ('geolocation' in navigator) {
      setGpsEnabled(true);
      setRecordingPath([]);
      
      gpsWatchIdRef.current = navigator.geolocation.watchPosition(
        (position) => {
          const newPoint = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            timestamp: Date.now()
          };
          setCurrentPosition(newPoint);
          setRecordingPath(prev => [...prev, newPoint]);
        },
        (error) => {
          console.error('GPS error:', error);
          toast.error('GPS unavailable');
        },
        { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
      );
    } else {
      toast.error('GPS not supported on this device');
    }
  };
  
  // Stop GPS tracking
  const stopGPSTracking = () => {
    if (gpsWatchIdRef.current) {
      navigator.geolocation.clearWatch(gpsWatchIdRef.current);
      gpsWatchIdRef.current = null;
    }
    setGpsEnabled(false);
  };
  
  // Calculate distance from GPS path (in meters)
  const calculateDistance = (path) => {
    if (path.length < 2) return 0;
    
    let totalDistance = 0;
    for (let i = 1; i < path.length; i++) {
      const lat1 = path[i-1].lat * Math.PI / 180;
      const lat2 = path[i].lat * Math.PI / 180;
      const dLat = (path[i].lat - path[i-1].lat) * Math.PI / 180;
      const dLng = (path[i].lng - path[i-1].lng) * Math.PI / 180;
      
      const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                Math.cos(lat1) * Math.cos(lat2) *
                Math.sin(dLng/2) * Math.sin(dLng/2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
      
      totalDistance += 6371000 * c; // Earth's radius in meters
    }
    
    return Math.round(totalDistance);
  };
  
  // Start Recording
  const startRecording = async () => {
    if (!featuresAvailable) {
      toast.error('Hardware not detected. Enable Demo Mode to record.');
      return;
    }
    
    try {
      // Check if getUserMedia is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        toast.error('Microphone access is not supported in this browser. Try Chrome or Edge.');
        return;
      }
      
      // Request microphone permission with specific constraints
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        } 
      });
      
      // Check for MediaRecorder support
      if (!window.MediaRecorder) {
        toast.error('Recording is not supported in this browser. Try Chrome or Edge.');
        stream.getTracks().forEach(track => track.stop());
        return;
      }
      
      // Determine best supported audio format
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') 
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : MediaRecorder.isTypeSupported('audio/mp4')
        ? 'audio/mp4'
        : '';
      
      const options = mimeType ? { mimeType } : {};
      
      mediaRecorderRef.current = new MediaRecorder(stream, options);
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType || 'audio/webm' });
        saveRecording(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorderRef.current.onerror = (event) => {
        console.error('MediaRecorder error:', event.error);
        toast.error('Recording error occurred');
        setIsRecording(false);
      };
      
      mediaRecorderRef.current.start(1000); // Collect data every second
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start GPS tracking
      startGPSTracking();
      
      // Start timer
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
      toast.success('Recording started');
    } catch (error) {
      console.error('Recording error:', error);
      
      // Provide specific error messages based on error type
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        toast.error('Microphone permission denied. Please allow microphone access in your browser settings.');
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        toast.error('No microphone found. Please connect a microphone and try again.');
      } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
        toast.error('Microphone is in use by another application. Please close other apps using the microphone.');
      } else if (error.name === 'OverconstrainedError') {
        toast.error('Microphone does not meet requirements. Trying with default settings...');
        // Retry with basic constraints
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          mediaRecorderRef.current = new MediaRecorder(stream);
          audioChunksRef.current = [];
          
          mediaRecorderRef.current.ondataavailable = (event) => {
            if (event.data.size > 0) {
              audioChunksRef.current.push(event.data);
            }
          };
          
          mediaRecorderRef.current.onstop = () => {
            const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
            saveRecording(audioBlob);
            stream.getTracks().forEach(track => track.stop());
          };
          
          mediaRecorderRef.current.start(1000);
          setIsRecording(true);
          setRecordingTime(0);
          startGPSTracking();
          recordingIntervalRef.current = setInterval(() => {
            setRecordingTime(prev => prev + 1);
          }, 1000);
          toast.success('Recording started with default microphone');
          return;
        } catch (retryError) {
          toast.error('Could not access microphone. Please check your device settings.');
        }
      } else if (error.name === 'SecurityError') {
        toast.error('Microphone access blocked. This site requires HTTPS for microphone access.');
      } else {
        toast.error(`Microphone error: ${error.message || 'Unknown error'}. Please check your browser permissions.`);
      }
    }
  };
  
  // Stop Recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      // Stop timer
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
      
      // Stop GPS
      stopGPSTracking();
      
      toast.success('Recording saved');
    }
  };
  
  // Save Recording to localStorage
  const saveRecording = (audioBlob) => {
    const now = new Date();
    const timestamp = now.toISOString().replace(/[:.]/g, '-').slice(0, 19);
    const filename = `CrewZ-${timestamp}`;
    
    // Convert blob to base64 for localStorage storage
    const reader = new FileReader();
    reader.onloadend = () => {
      const newRecording = {
        id: Date.now().toString(),
        filename,
        date: now.toISOString(),
        duration: recordingTime,
        audioData: reader.result,
        distance: calculateDistance(recordingPath),
        gpsPath: recordingPath.length > 0 ? recordingPath : null
      };
      
      const updatedRecordings = [newRecording, ...recordings];
      setRecordings(updatedRecordings);
      localStorage.setItem('cox_recordings', JSON.stringify(updatedRecordings));
      setRecordingPath([]);
    };
    reader.readAsDataURL(audioBlob);
  };
  
  // Play Recording
  const playRecording = (recording) => {
    if (playingId === recording.id) {
      // Stop playing
      if (audioPlayerRef.current) {
        audioPlayerRef.current.pause();
        audioPlayerRef.current.currentTime = 0;
      }
      setPlayingId(null);
    } else {
      // Start playing
      if (audioPlayerRef.current) {
        audioPlayerRef.current.pause();
      }
      
      const audio = new Audio(recording.audioData);
      audioPlayerRef.current = audio;
      
      audio.onended = () => setPlayingId(null);
      audio.play();
      setPlayingId(recording.id);
    }
  };
  
  // Delete Recording
  const deleteRecording = (id) => {
    const updatedRecordings = recordings.filter(r => r.id !== id);
    setRecordings(updatedRecordings);
    localStorage.setItem('cox_recordings', JSON.stringify(updatedRecordings));
    toast.success('Recording deleted');
  };
  
  // Download Recording
  const downloadRecording = (recording) => {
    const link = document.createElement('a');
    link.href = recording.audioData;
    link.download = `${recording.filename}.webm`;
    link.click();
    toast.success('Recording downloaded');
  };
  
  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-900 flex flex-col transition-colors">
      <TopNav />
      
      <div className="flex-1 py-6 px-4">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-3xl font-extrabold tracking-tighter text-zinc-900 dark:text-white" style={{ fontFamily: 'Archivo, sans-serif' }}>
              Coxswain
            </h1>
            <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
              CREWZ Cox voice recording and amplification control
            </p>
          </div>
          
          {/* Hardware Status Banner */}
          {!checkingHardware && !hardwareDetected && !demoMode && (
            <div className="bg-amber-50 dark:bg-amber-900/30 border-2 border-amber-300 dark:border-amber-800 rounded-xl p-4 mb-6">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900 rounded-full flex items-center justify-center flex-shrink-0">
                  <Volume2 className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <h3 className="font-bold text-amber-800 dark:text-amber-300">Hardware Not Detected</h3>
                  <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
                    No CREWZ Cox amplifier or external audio output detected. 
                    Enable Demo Mode below to test features without hardware.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Quick Settings Row - Demo Mode, Cox Mode, GPS Tracking */}
          <div className="bg-white dark:bg-zinc-800 border-2 border-zinc-200 dark:border-zinc-700 rounded-xl p-4 mb-6">
            <div className="grid grid-cols-3 gap-4">
              {/* Demo Mode */}
              <div className="text-center">
                <button
                  onClick={toggleDemoMode}
                  data-testid="demo-mode-toggle"
                  className={`w-full py-3 px-2 rounded-lg transition-all ${
                    demoMode 
                      ? 'bg-green-500 text-white' 
                      : 'bg-zinc-100 dark:bg-zinc-700 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-600'
                  }`}
                >
                  <div className="flex flex-col items-center gap-1">
                    <Play className={`w-5 h-5 ${demoMode ? 'text-white' : 'text-zinc-500 dark:text-zinc-400'}`} />
                    <span className="text-xs font-bold uppercase tracking-wide">Demo</span>
                  </div>
                </button>
                <p className="text-xs text-zinc-400 mt-1">{demoMode ? 'On' : 'Off'}</p>
              </div>
              
              {/* Cox Mode */}
              <div className="text-center">
                <button
                  onClick={toggleCoxMode}
                  data-testid="cox-mode-toggle"
                  disabled={!featuresAvailable}
                  className={`w-full py-3 px-2 rounded-lg transition-all ${
                    coxModeEnabled && featuresAvailable
                      ? 'bg-blue-500 text-white' 
                      : 'bg-zinc-100 dark:bg-zinc-700 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-600'
                  } ${!featuresAvailable ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="flex flex-col items-center gap-1">
                    <Volume2 className={`w-5 h-5 ${coxModeEnabled && featuresAvailable ? 'text-white' : 'text-zinc-500 dark:text-zinc-400'}`} />
                    <span className="text-[10px] font-bold uppercase tracking-wide leading-tight">Cox<br/>Mode</span>
                  </div>
                </button>
                <p className="text-xs text-zinc-400 mt-1">{coxModeEnabled ? 'On' : 'Off'}</p>
              </div>
              
              {/* GPS Tracking */}
              <div className="text-center">
                <div
                  className={`w-full py-3 px-2 rounded-lg transition-all ${
                    gpsEnabled 
                      ? 'bg-amber-500 text-white' 
                      : 'bg-zinc-100 dark:bg-zinc-700 text-zinc-600 dark:text-zinc-300'
                  }`}
                >
                  <div className="flex flex-col items-center gap-1">
                    <MapPin className={`w-5 h-5 ${gpsEnabled ? 'text-white' : 'text-zinc-500 dark:text-zinc-400'}`} />
                    <span className="text-xs font-bold uppercase tracking-wide">GPS</span>
                  </div>
                </div>
                <p className="text-xs text-zinc-400 mt-1">{gpsEnabled ? 'Active' : 'Auto'}</p>
              </div>
            </div>
          </div>
          
          {/* Volume Controls - Only visible when Cox Mode enabled AND features available */}
          {coxModeEnabled && featuresAvailable && (
            <div className="bg-white dark:bg-zinc-800 border-2 border-zinc-200 dark:border-zinc-700 rounded-xl p-6 mb-6">
              <h3 className="font-bold text-zinc-800 dark:text-white text-center mb-4">Boat Volume Control</h3>
              
              {/* Volume Level Indicator */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-zinc-500 dark:text-zinc-400">Level</span>
                  <span className="text-sm font-bold text-zinc-700 dark:text-zinc-300" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    {isMuted ? 'MUTED' : `${volume}%`}
                  </span>
                </div>
                <div className="h-3 bg-zinc-200 dark:bg-zinc-700 rounded-full overflow-hidden">
                  <div 
                    className={`h-full transition-all duration-200 rounded-full ${
                      isMuted ? 'bg-red-500' : volume > 70 ? 'bg-green-500' : volume > 30 ? 'bg-blue-500' : 'bg-amber-500'
                    }`}
                    style={{ width: `${isMuted ? 0 : volume}%` }}
                  />
                </div>
              </div>
              
              {/* Control Buttons */}
              <div className="flex justify-center items-center gap-4">
                {/* Volume Down (-) */}
                <button
                  onClick={() => adjustVolume('down')}
                  data-testid="volume-down-btn"
                  disabled={volume === 0}
                  className="group relative w-16 h-16 rounded-full transition-all duration-300 hover:scale-110 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  <div className="absolute inset-0 rounded-full border-4 border-blue-500 bg-gradient-to-b from-gray-100 to-gray-200 dark:from-zinc-700 dark:to-zinc-800 shadow-lg group-hover:shadow-xl transition-shadow" />
                  <div className="absolute inset-2 rounded-full bg-gradient-to-b from-white to-gray-100 dark:from-zinc-600 dark:to-zinc-700 opacity-80" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Minus className="w-8 h-8 text-blue-500 group-hover:scale-110 transition-transform" />
                  </div>
                </button>
                
                {/* Mute Button */}
                <button
                  onClick={toggleMute}
                  data-testid="mute-btn"
                  className={`group relative w-20 h-20 rounded-full transition-all duration-300 hover:scale-110 active:scale-95`}
                >
                  <div className={`absolute inset-0 rounded-full border-4 ${
                    isMuted ? 'border-red-500' : 'border-green-500'
                  } bg-gradient-to-b from-gray-100 to-gray-200 dark:from-zinc-700 dark:to-zinc-800 shadow-lg group-hover:shadow-xl transition-shadow`} />
                  <div className="absolute inset-2 rounded-full bg-gradient-to-b from-white to-gray-100 dark:from-zinc-600 dark:to-zinc-700 opacity-80" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    {isMuted ? (
                      <VolumeX className="w-10 h-10 text-red-500 group-hover:scale-110 transition-transform" />
                    ) : (
                      <Volume2 className="w-10 h-10 text-green-500 group-hover:scale-110 transition-transform" />
                    )}
                  </div>
                </button>
                
                {/* Volume Up (+) */}
                <button
                  onClick={() => adjustVolume('up')}
                  data-testid="volume-up-btn"
                  disabled={volume === 100}
                  className="group relative w-16 h-16 rounded-full transition-all duration-300 hover:scale-110 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  <div className="absolute inset-0 rounded-full border-4 border-blue-500 bg-gradient-to-b from-gray-100 to-gray-200 dark:from-zinc-700 dark:to-zinc-800 shadow-lg group-hover:shadow-xl transition-shadow" />
                  <div className="absolute inset-2 rounded-full bg-gradient-to-b from-white to-gray-100 dark:from-zinc-600 dark:to-zinc-700 opacity-80" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Plus className="w-8 h-8 text-blue-500 group-hover:scale-110 transition-transform" />
                  </div>
                </button>
              </div>
              
              {/* Button Labels */}
              <div className="flex justify-center items-center gap-4 mt-3">
                <span className="w-16 text-center text-xs font-bold text-zinc-400 uppercase">Down</span>
                <span className="w-20 text-center text-xs font-bold text-zinc-400 uppercase">{isMuted ? 'Unmute' : 'Mute'}</span>
                <span className="w-16 text-center text-xs font-bold text-zinc-400 uppercase">Up</span>
              </div>
            </div>
          )}
          
          {/* Voice Recording Section */}
          <div className={`bg-white dark:bg-zinc-800 border-2 rounded-xl p-4 mb-4 transition-opacity ${
            featuresAvailable ? 'border-zinc-200 dark:border-zinc-700' : 'border-zinc-100 dark:border-zinc-800 opacity-50'
          }`}>
            <h3 className="font-bold text-zinc-800 dark:text-white text-center mb-3 text-sm">Voice Recording</h3>
            
            {/* Instruction when features not available */}
            {!featuresAvailable && (
              <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg p-2 mb-3 text-center">
                <p className="text-xs text-amber-700 dark:text-amber-300">
                  Enable <strong>Demo Mode</strong> above to use recording features
                </p>
              </div>
            )}
            
            {/* Microphone Icon with Timer */}
            <div className="flex flex-col items-center">
              <button
                onClick={isRecording ? stopRecording : startRecording}
                data-testid="record-btn"
                disabled={!featuresAvailable && !isRecording}
                className={`relative transition-all duration-300 ${
                  featuresAvailable ? 'hover:scale-105 active:scale-95' : 'cursor-not-allowed'
                } ${
                  isRecording ? 'animate-pulse' : ''
                }`}
              >
                <VintageMicIcon 
                  className="w-24 h-32" 
                  recording={isRecording}
                />
                
                {/* Red Stopwatch Timer - overlaid on mic */}
                {isRecording && (
                  <div className="absolute bottom-8 left-1/2 -translate-x-1/2 bg-red-600 text-white px-2 py-0.5 rounded-full flex items-center gap-1 shadow-lg">
                    <Clock className="w-3 h-3" />
                    <span className="font-mono font-bold text-xs">{formatTime(recordingTime)}</span>
                  </div>
                )}
              </button>
              
              <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-2">
                {isRecording ? 'Tap to stop' : 'Tap to record'}
              </p>
              
              {/* Recording indicator */}
              {isRecording && (
                <div className="flex items-center gap-2 mt-1">
                  <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                  <span className="text-red-600 dark:text-red-400 font-bold text-xs">Recording...</span>
                  {gpsEnabled && recordingPath.length > 0 && (
                    <span className="text-zinc-500 dark:text-zinc-400 text-xs">
                      | {calculateDistance(recordingPath)}m
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
          
          {/* Saved Recordings Section - Separate Box Below */}
          <div className="bg-white dark:bg-zinc-800 border-2 border-zinc-200 dark:border-zinc-700 rounded-xl p-4">
            <h3 className="font-bold text-zinc-800 dark:text-white mb-3">Saved Recordings</h3>
            
            {/* Scrollable recordings list */}
            <div className="max-h-64 overflow-y-auto">
              {recordings.length === 0 ? (
                <div className="text-center py-6">
                  <div className="w-12 h-12 bg-zinc-100 dark:bg-zinc-700 rounded-full flex items-center justify-center mx-auto mb-3">
                    <VintageMicIcon className="w-6 h-6" />
                  </div>
                  <p className="text-zinc-500 dark:text-zinc-400 text-sm">No recordings yet</p>
                  <p className="text-xs text-zinc-400 dark:text-zinc-500 mt-1">Tap the microphone to start</p>
                </div>
              ) : (
                <div className="space-y-3" data-testid="recordings-list">
                  {recordings.map((recording) => (
                    <div
                      key={recording.id}
                      className={`border-2 rounded-xl p-3 transition-colors ${
                        playingId === recording.id 
                          ? 'border-green-400 dark:border-green-600 bg-green-50 dark:bg-green-900/30' 
                          : 'border-zinc-200 dark:border-zinc-600'
                      }`}
                      data-testid={`recording-${recording.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-bold text-zinc-800 dark:text-zinc-200 text-sm truncate" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                            {recording.filename}
                          </h4>
                          <div className="flex items-center gap-3 mt-1 text-xs text-zinc-500 dark:text-zinc-400">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatTime(recording.duration)}
                            </span>
                            <span>{formatDate(recording.date)}</span>
                            {recording.distance > 0 && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {recording.distance}m
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 ml-2">
                          {/* Play/Pause */}
                          <button
                            onClick={() => playRecording(recording)}
                            className={`p-2 rounded-lg transition-colors ${
                              playingId === recording.id 
                                ? 'bg-green-500 text-white' 
                                : 'bg-zinc-100 dark:bg-zinc-700 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-600'
                            }`}
                            data-testid={`play-${recording.id}`}
                          >
                            {playingId === recording.id ? (
                              <Pause className="w-4 h-4" />
                            ) : (
                              <Play className="w-4 h-4" />
                            )}
                          </button>
                          
                          {/* Download */}
                          <button
                            onClick={() => downloadRecording(recording)}
                            className="p-2 bg-zinc-100 dark:bg-zinc-700 text-zinc-600 dark:text-zinc-300 rounded-lg hover:bg-zinc-200 dark:hover:bg-zinc-600 transition-colors"
                            data-testid={`download-${recording.id}`}
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                          </button>
                          
                          {/* Delete */}
                          <button
                            onClick={() => deleteRecording(recording.id)}
                            className="p-2 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
                            data-testid={`delete-${recording.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      
                      {/* GPS Path Preview */}
                      {recording.gpsPath && recording.gpsPath.length > 1 && (
                        <div className="mt-2 p-2 bg-zinc-50 dark:bg-zinc-700 rounded-lg">
                          <div className="flex items-center gap-2 text-xs text-zinc-500 dark:text-zinc-400">
                            <MapPin className="w-3 h-3 text-green-500" />
                            <span>GPS route recorded: {recording.gpsPath.length} points</span>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
